import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {queryOptions, useQuery, useQueryClient, type QueryClient} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'

import type {ImplicitContextPage} from '../contexts/ImplicitContextPageContext'
import {fetchImplicitReferences} from './fetch-implicit-references'
import type {ImplicitReference} from './implicit-context'
import {githubUrlAttachment, implicitReferencesToAttachments} from './implicit-context'

/**
 * Maps resolved references to attachments, falling back to a generic
 * `github_url` anchor for the current page when nothing renderable resolved.
 * Mirrors copilot-chat's `addUrlAsImplicitReference`: it covers both
 * non-repository pages (which never hit the endpoint) and repository pages the
 * endpoint can't resolve to a renderable reference.
 */
export function attachmentsForPage(refs: ImplicitReference[], page: ImplicitContextPage | null): MessageAttachment[] {
  const mapped = implicitReferencesToAttachments(refs)
  if (mapped.length > 0) return mapped
  if (page) {
    return [githubUrlAttachment(page.url, page.title)]
  }
  return []
}

/**
 * Submit-time bound on implicit-context resolution. A fast submit awaits the
 * in-flight request so the page context isn't dropped, but a stalled endpoint
 * can't strand the message: once this deadline passes we fall back to the same
 * `github_url` anchor the error path uses, keeping every failure mode consistent.
 */
export const IMPLICIT_CONTEXT_RESOLVE_TIMEOUT_MS = 3000

/** Races a promise against a deadline, resolving to `fallback` if it doesn't settle in time. */
async function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<T>(resolve => {
    timer = setTimeout(() => resolve(fallback), ms)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Shared query definition so the hook and the imperative resolver stay in sync. */
function implicitContextQuery(page: ImplicitContextPage | null) {
  // Key on the fetch-relevant fields only; `title` rides the page for the
  // github_url fallback label but doesn't affect the endpoint request, so
  // excluding it avoids redundant refetches when only the title changes.
  const key = page ? {owner: page.owner, repo: page.repo, url: page.url} : null
  return queryOptions({
    queryKey: ['copilot-implicit-context', key],
    queryFn: ({signal}) => (key ? fetchImplicitReferences(key, signal) : Promise.resolve([])),
    enabled: page != null,
    staleTime: 60 * 1000,
  })
}

/**
 * Resolves the endpoint references for a page through the shared query cache.
 *
 * Every failure mode resolves to an empty list so callers can apply their own
 * page fallback without needing to duplicate the timeout and error handling.
 */
export async function resolveImplicitContextReferences(
  queryClient: QueryClient,
  page: ImplicitContextPage | null,
): Promise<ImplicitReference[]> {
  if (!page) return []

  try {
    return await withTimeout(
      queryClient.fetchQuery(implicitContextQuery(page)),
      IMPLICIT_CONTEXT_RESOLVE_TIMEOUT_MS,
      [],
    )
  } catch {
    return []
  }
}

/** Resolves a page into attachments, including the generic URL fallback. */
export async function resolveImplicitContextAttachments(
  queryClient: QueryClient,
  page: ImplicitContextPage | null,
): Promise<MessageAttachment[]> {
  return attachmentsForPage(await resolveImplicitContextReferences(queryClient, page), page)
}

export interface ImplicitContextAttachments {
  /** The implicit attachments known at render time (empty while the query is pending). */
  attachments: MessageAttachment[]
  /**
   * Resolves the implicit attachments, awaiting the in-flight request when it
   * hasn't settled yet. Call this at submit time so a fast submission can't race
   * the query and silently drop the page context.
   */
  resolveAttachments: () => Promise<MessageAttachment[]>
}

/**
 * Resolves the current page into AHP attachments for implicit context.
 *
 * Backed by TanStack Query so responses are deduped and cached per page URL,
 * and the in-flight request is aborted on unmount / URL change via the
 * query-provided `AbortSignal` — avoiding the stale-response races the older
 * copilot-chat implementation is prone to. Disabled (returns `[]`) when there is
 * no current page, so nothing is fetched until a page is known.
 */
export function useImplicitContextAttachments(
  page: ImplicitContextPage | null,
  getLatestPage?: () => ImplicitContextPage | null,
): ImplicitContextAttachments {
  const queryClient = useQueryClient()
  const {data, status} = useQuery(implicitContextQuery(page))

  // While the query is pending, `data` is `undefined`; treating that as a
  // resolved empty result would render the generic URL fallback and then swap it
  // for the richer attachment, flashing a chip on every navigation. Only compute
  // attachments (including the fallback) once the query has settled — success or
  // error. On error we still surface the URL fallback, matching immersive.
  const attachments = useMemo(
    () => (status === 'pending' ? [] : attachmentsForPage(data ?? [], page)),
    [data, status, page],
  )

  const resolveAttachments = useCallback(async () => {
    // Resolve against the freshest page so a submit right after navigation —
    // before the reactive value settles, or after a router hash change that
    // fired no event — carries the current page's context rather than the
    // previous one. A resolver's explicit `null` means "there is no current
    // page" (e.g. navigating to a non-repository page) and must be honored, so
    // only fall back to the reactive `page` when no resolver was provided.
    const latestPage = getLatestPage ? getLatestPage() : page
    return resolveImplicitContextAttachments(queryClient, latestPage)
  }, [page, getLatestPage, queryClient])

  return {attachments, resolveAttachments}
}
